/* RESPONSIBLE TEAM: team-knowledge-foundations */
import type IntlService from 'ember-intl/services/intl';
import type ContentSection from 'embercom/models/content-service/content-section';
import type ContentStatistic from 'embercom/models/outbound/content-statistic';
import { statisticKeys } from 'embercom/models/data/outbound/constants';
import { humanReadableObjectNames } from 'embercom/models/data/matching-system/matching-constants';
import { capitalize } from '@ember/string';

function getContentTypeName(section: ContentSection | undefined): string {
  if (!section) {
    return '';
  }

  return capitalize(humanReadableObjectNames[section.entityType]);
}

function extractStatisticValueFromStats(
  stats: ContentStatistic[],
  statName: keyof typeof statisticKeys,
): number | null {
  let statisticKey = statisticKeys[statName];
  let statistic = stats.find((stat) => stat && stat.key === statisticKey);
  return statistic?.value ?? null;
}

function calculateResolutionRateFromStats(stats: ContentStatistic[]): number | null {
  let receipts = extractStatisticValueFromStats(stats, 'receipts');
  let resolutions = extractStatisticValueFromStats(stats, 'finHelpfulAnswers');

  //if there are no receipts or resolutions, or if there are no receipts, return 0
  if (receipts === null || resolutions === null || receipts === 0) {
    return 0;
  }

  return (resolutions / receipts) * 100;
}

export interface SectionData {
  name: string;
  involvement: number | null;
  resolutionRate: number | null;
  updatedTime: number | null;
}

export function getSectionData(
  section: ContentSection,
  intl: IntlService,
  stats: ContentStatistic[],
): SectionData {
  return {
    name:
      section.relatedContent?.title ||
      intl.t('knowledge-hub.content-editor.side-by-side-review.fallback-content-source'),
    involvement: extractStatisticValueFromStats(stats, 'finStepReached'),
    resolutionRate: calculateResolutionRateFromStats(stats),
    updatedTime: section.relatedContent?.updatedAt
      ? new Date(section.relatedContent.updatedAt).getTime()
      : null,
  };
}

export function buildUsageComparison(sourceData: any, targetData: any, intl: IntlService): string {
  let sourceInvolvement = sourceData.involvement ?? 0;
  let targetInvolvement = targetData.involvement ?? 0;

  if (sourceInvolvement === targetInvolvement) {
    return intl.t(
      'knowledge-hub.content-editor.side-by-side-review.duplicate-review.usage-both-equal',
    );
  }

  let contentA = sourceInvolvement > targetInvolvement ? sourceData : targetData;
  let contentB = sourceInvolvement > targetInvolvement ? targetData : sourceData;

  return intl.t(
    'knowledge-hub.content-editor.side-by-side-review.duplicate-review.usage-comparison-more',
    {
      ContentA: contentA.name,
      ContentB: contentB.name,
    },
  );
}

export function buildResolutionComparison(
  sourceData: any,
  targetData: any,
  intl: IntlService,
): string {
  let sourceResolution = sourceData.resolutionRate ?? 0;
  let targetResolution = targetData.resolutionRate ?? 0;

  if (sourceResolution === targetResolution) {
    return intl.t(
      'knowledge-hub.content-editor.side-by-side-review.duplicate-review.resolution-both-equal',
    );
  }

  let contentA = sourceResolution > targetResolution ? sourceData : targetData;
  let contentB = sourceResolution > targetResolution ? targetData : sourceData;

  return intl.t(
    'knowledge-hub.content-editor.side-by-side-review.duplicate-review.resolution-comparison-higher',
    {
      ContentA: contentA.name,
      ContentB: contentB.name,
    },
  );
}

export function buildUpdateTimeComparison(
  sourceData: any,
  targetData: any,
  intl: IntlService,
): string {
  let sourceTime = sourceData.updatedTime;
  let targetTime = targetData.updatedTime;

  //By default, source data will be more recent.
  let contentA = sourceData;
  let contentB = targetData;

  if ((!sourceTime && targetTime) || (sourceTime && targetTime && sourceTime < targetTime)) {
    contentA = targetData;
    contentB = sourceData;
  }

  return intl.t(
    'knowledge-hub.content-editor.side-by-side-review.duplicate-review.update-comparison-more',
    {
      ContentA: contentA.name,
      ContentB: contentB.name,
    },
  );
}

// Get the base reason for duplicate content review
export function getDuplicateReviewBaseReason(
  sourceSection: ContentSection,
  targetSection: ContentSection,
  intl: IntlService,
): string {
  let sourceTitle =
    sourceSection.relatedContent?.title ||
    intl.t('knowledge-hub.content-editor.side-by-side-review.fallback-content-source');
  let targetTitle =
    targetSection.relatedContent?.title ||
    intl.t('knowledge-hub.content-editor.side-by-side-review.fallback-content-target');

  let sourceType = getContentTypeName(sourceSection);
  let targetType = getContentTypeName(targetSection);

  return intl.t('knowledge-hub.content-editor.side-by-side-review.duplicate-review.reason', {
    contentNameA: sourceTitle,
    contentTypeA: sourceType,
    contentNameB: targetTitle,
    contentTypeB: targetType,
  });
}

// Get the bullet points for duplicate content review
export function getDuplicateReviewBulletPoints(
  sourceSection: ContentSection,
  targetSection: ContentSection,
  intl: IntlService,
  sourceStats: ContentStatistic[],
  targetStats: ContentStatistic[],
): string[] {
  if (!sourceSection || !targetSection) {
    return [];
  }

  let sourceData = getSectionData(sourceSection, intl, sourceStats);
  let targetData = getSectionData(targetSection, intl, targetStats);

  let bulletPoints: string[] = [];

  // Each bullet point is independent - they may have equal usage but different resolution rates
  bulletPoints.push(buildUsageComparison(sourceData, targetData, intl));
  bulletPoints.push(buildResolutionComparison(sourceData, targetData, intl));
  bulletPoints.push(buildUpdateTimeComparison(sourceData, targetData, intl));

  return bulletPoints.filter(Boolean);
}

/**
 * Escapes special regex characters in a string to prevent ReDoS attacks.
 * This ensures that user input can be safely used in RegExp constructors.
 */
function escapeRegExpSpecialChars(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Validates that a section hash is in the expected format (hexadecimal string).
 * This provides an additional layer of security against malicious input.
 */
function isValidSectionHash(hash: string): boolean {
  // Section hashes should be hexadecimal strings (typically 64 characters for SHA-256)
  return /^[a-f0-9]+$/i.test(hash) && hash.length > 0 && hash.length <= 128;
}

/**
 * Replaces content placeholders in review reasons with actual content titles.
 * Supports both new hash-based format and legacy Content A/Content B format.
 *
 * Security note: This function validates and escapes section hash inputs to prevent ReDoS attacks.
 */
export function replaceContentPlaceholders(
  reviewReason: string,
  sourceTitle: string,
  targetTitle: string,
  sourceSectionHash?: string,
  targetSectionHash?: string,
): string {
  let result = reviewReason;

  // Replace section hash placeholders with actual titles
  if (sourceSectionHash && isValidSectionHash(sourceSectionHash)) {
    // Escape the hash to prevent regex injection attacks
    let escapedHash = escapeRegExpSpecialChars(sourceSectionHash);
    let sourceHashPattern = new RegExp(
      `\\{\\{entity\\.content_section\\.section_hash\\.${escapedHash}\\}\\}`,
      'gi',
    );
    let replacement = `"${sourceTitle}"`;
    result = result.replace(sourceHashPattern, replacement);
  }

  if (targetSectionHash && isValidSectionHash(targetSectionHash)) {
    // Escape the hash to prevent regex injection attacks
    let escapedHash = escapeRegExpSpecialChars(targetSectionHash);
    let targetHashPattern = new RegExp(
      `\\{\\{entity\\.content_section\\.section_hash\\.${escapedHash}\\}\\}`,
      'gi',
    );
    let replacement = `"${targetTitle}"`;
    result = result.replace(targetHashPattern, replacement);
  }

  // Also handle legacy Content A/Content B format for backward compatibility
  let sourceReplacement = `"${sourceTitle}"`;
  let targetReplacement = `"${targetTitle}"`;
  return result
    .replace(/\bContent A\b/gi, sourceReplacement)
    .replace(/\bContent B\b/gi, targetReplacement);
}
